import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import { cashSettlementApi } from './cashSettlementApi';

// Dummy data for development/testing - COMMENTED OUT
// Set USE_DUMMY_DATA to false to use real API calls
// const USE_DUMMY_DATA = false;

// const DUMMY_SITES = [
//   {
//     site_id: 'site_001',
//     site_name: 'Main Branch',
//     name: 'Main Branch',
//   },
//   {
//     site_id: 'site_002',
//     site_name: 'Downtown Location',
//     name: 'Downtown Location',
//   },
//   {
//     site_id: 'site_003',
//     site_name: 'Airport Branch',
//     name: 'Airport Branch',
//   },
// ];

// const DUMMY_SHIFTS = [
//   {
//     ss_id: 'shift_20240204_001',
//     shift_name: 'Morning Shift',
//     site_id: 'site_001',
//     status: 'active',
//     opening_float: 5000.00,
//     total_cash_sales: 12500.50,
//   },
//   {
//     ss_id: 'shift_20240204_002',
//     shift_name: 'Evening Shift',
//     site_id: 'site_001',
//     status: 'active',
//     opening_float: 5000.00,
//     total_cash_sales: 18500.75,
//   },
// ];

// const DUMMY_SETTLEMENTS = {
//   'shift_20240204_001': {
//     ss_id: 'shift_20240204_001',
//     opening_float: 5000.00,
//     total_cash_sales: 12500.50,
//     expected_cash: 17500.50,
//     counted_cash: 17450.00,
//     total_paid_outs: 250.00,
//     discrepancy: -300.50, // Negative variance
//     status: 'active',
//     audit_trail: [
//       {
//         action: 'Shift opened',
//         timestamp: '2024-02-04T08:00:00Z',
//         user: 'Manager John Doe',
//       },
//       {
//         action: 'Safe drop recorded',
//         timestamp: '2024-02-04T14:30:00Z',
//         user: 'Cashier Jane Smith',
//       },
//       {
//         action: 'Paid-out created: Office supplies',
//         timestamp: '2024-02-04T15:00:00Z',
//         user: 'Manager John Doe',
//       },
//       {
//         action: 'Settlement calculated',
//         timestamp: '2024-02-04T22:00:00Z',
//         user: 'System',
//       },
//     ],
//   },
//   'shift_20240204_002': {
//     ss_id: 'shift_20240204_002',
//     opening_float: 5000.00,
//     total_cash_sales: 18500.75,
//     expected_cash: 23500.75,
//     counted_cash: 23500.75,
//     total_paid_outs: 0.00,
//     discrepancy: 0.00, // Perfect match
//     status: 'active',
//     audit_trail: [
//       {
//         action: 'Shift opened',
//         timestamp: '2024-02-04T16:00:00Z',
//         user: 'Manager John Doe',
//       },
//       {
//         action: 'Safe drop recorded',
//         timestamp: '2024-02-04T22:00:00Z',
//         user: 'Cashier Jane Smith',
//       },
//       {
//         action: 'Settlement calculated',
//         timestamp: '2024-02-04T22:30:00Z',
//         user: 'System',
//       },
//     ],
//   },
// };

/** Returns YYYY-MM-DD for today (local). */
const getTodayYYYYMMDD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Returns YYYY-MM-DD from shift_start_timestamp (ISO string). */
const getDateFromShiftTimestamp = (ts) => {
  if (!ts) return null;
  if (typeof ts === 'string' && /^\d{4}-\d{2}-\d{2}/.test(ts)) return ts.slice(0, 10);
  try {
    return new Date(ts).toISOString().slice(0, 10);
  } catch {
    return null;
  }
};

/** Format timestamp to time string e.g. "9:00 AM", "5:00 PM". */
const formatTimeFromTimestamp = (ts) => {
  if (!ts) return '--';
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return '--';
  }
};

const ShiftSettlementSummary = () => {
  // Log screen view
  useScreenLogging('ShiftSettlementSummary', {
    component: 'ShiftSettlementSummary',
    screenType: 'shift-reconciliation',
  });

  const [selectedSite, setSelectedSite] = useState('');
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [shiftDate, setShiftDate] = useState('');
  const [sites, setSites] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [settlement, setSettlement] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [managerAcknowledged, setManagerAcknowledged] = useState(false);
  const [finalizeFeedback, setFinalizeFeedback] = useState({ success: '', error: '' });

  // Fetch sites
  useEffect(() => {
    const fetchSites = async () => {
      // if (USE_DUMMY_DATA) {
      //   // Use dummy sites
      //   setSites(DUMMY_SITES);
      //   return;
      // }

      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites) ? response.sites : Array.isArray(response) ? response : [];
        setSites(list.sort((a, b) => (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')));
        
        // Log successful API call
        loggingService.logInfo(
          'shift_reconciliation_load_sites',
          `Successfully loaded ${list.length} sites for shift reconciliation`,
          {
            endpoint: '/sites',
            method: 'GET',
            siteCount: list.length,
          }
        ).catch(logError => {
          console.warn('[ShiftSettlementSummary] Failed to log event:', logError);
        });
      } catch (error) {
        console.error('Error fetching sites:', error);
        // Fallback to dummy sites on error - COMMENTED OUT
        // setSites(DUMMY_SITES);
        
        // Log API error
        loggingService.logError(
          'shift_reconciliation_load_sites_error',
          `Failed to load sites for shift reconciliation: ${error.message || 'Unknown error'}`,
          error,
          {
            endpoint: '/sites',
            method: 'GET',
          }
        ).catch(logError => {
          console.warn('[ShiftSettlementSummary] Failed to log error:', logError);
        });
      }
    };
    fetchSites();
  }, []);

  // Prefill Site, Shift and date from current user attendance (GET /attendance/get-current/v2)
  useEffect(() => {
    const applyCurrentShift = async () => {
      try {
        const result = await cashSettlementApi.getCurrentShiftData();
        if (result.success && result.shift) {
          const shift = result.shift;
          setShifts(result.shifts || [shift]);
          setSelectedShiftId(shift.ss_id);
          setSelectedSite(shift.site_id || '');
          const dateStr = getDateFromShiftTimestamp(shift.shift_start_timestamp);
          if (dateStr) setShiftDate(dateStr);
          
          // Log successful API call
          loggingService.logInfo(
            'shift_reconciliation_get_current_shift',
            `Successfully loaded current shift ${shift.ss_id} for shift reconciliation`,
            {
              endpoint: '/attendance/get-current/v2',
              method: 'GET',
              shiftId: shift.ss_id,
              siteId: shift.site_id,
            }
          ).catch(logError => {
            console.warn('[ShiftSettlementSummary] Failed to log event:', logError);
          });
        } else {
          setShifts([]);
          setSelectedShiftId('');
        }
      } catch (error) {
        console.warn(' ShiftSettlementSummary: Could not prefill from current attendance:', error?.message || error);
        setShifts([]);
        setSelectedShiftId('');
        
        // Log API error
        loggingService.logError(
          'shift_reconciliation_get_current_shift_error',
          `Failed to get current shift for shift reconciliation: ${error.message || 'Unknown error'}`,
          error,
          {
            endpoint: '/attendance/get-current/v2',
            method: 'GET',
          }
        ).catch(logError => {
          console.warn('[ShiftSettlementSummary] Failed to log error:', logError);
        });
      }
    };
    applyCurrentShift();
  }, []);

  // Calculate settlement when shift is selected (uses GET /cash/shifts/{ss_id} as primary source)
  useEffect(() => {
    if (!selectedShiftId) {
      setSettlement(null);
      return;
    }

    calculateSettlement();
  }, [selectedShiftId]);

  const calculateSettlement = async () => {
    if (!selectedShiftId) return;

    setIsLoading(true);
    
    // if (USE_DUMMY_DATA) {
    //   // Use dummy data
    //   setTimeout(() => {
    //     const dummySettlement = DUMMY_SETTLEMENTS[selectedShiftId];
    //     if (dummySettlement) {
    //       setSettlement(dummySettlement);
    //     } else {
    //       // Fallback settlement if shift ID not found
    //       setSettlement({
    //         ss_id: selectedShiftId,
    //         opening_float: 5000.00,
    //         total_cash_sales: 10000.00,
    //         expected_cash: 15000.00,
    //         counted_cash: 14950.00,
    //         total_paid_outs: 50.00,
    //         discrepancy: -100.00,
    //         status: 'active',
    //         audit_trail: [
    //           {
    //             action: 'Shift opened',
    //             timestamp: new Date().toISOString(),
    //             user: 'Manager',
    //           },
    //         ],
    //       });
    //     }
    //     setIsLoading(false);
    //   }, 500); // Simulate API delay
    //   return;
    // }

    try {
      // Primary endpoint: GET /cash/shifts/{ss_id} (Lambda: innoworx-cash-shift-get, Table: cash-shifts)
      // Supplies: opening_float, total_cash_sales, manual_paid_out_amount, status, created_at, etc.
      const [cashShiftResult, drawerCountsResult, paidOutsResult] = await Promise.all([
        cashSettlementApi.getCashShift(selectedShiftId),
        cashSettlementApi.listDrawerCounts(selectedShiftId), // GET /cash/drawers?ss_id= — Actual Cash (Counted)
        cashSettlementApi.listPaidOuts(selectedShiftId),     // GET /cash/paid-outs?ss_id= — optional sum for Total Paid-Outs
      ]);
      
      // Log successful API calls
      loggingService.logInfo(
        'shift_reconciliation_calculate_settlement',
        `Successfully calculated settlement for shift ${selectedShiftId}`,
        {
          endpoint: '/cash/shifts',
          method: 'GET',
          shiftId: selectedShiftId,
          hasCashShift: cashShiftResult.success,
          drawerCount: drawerCountsResult.success ? (drawerCountsResult.drawerCounts?.length || 0) : 0,
          paidOutCount: paidOutsResult.success ? (paidOutsResult.paidOuts?.length || 0) : 0,
        }
      ).catch(logError => {
        console.warn('[ShiftSettlementSummary] Failed to log event:', logError);
      });

      const cashShift = cashShiftResult.success && cashShiftResult.data ? cashShiftResult.data : null;
      if (!cashShift) {
        throw new Error(cashShiftResult.error || 'No cash shift data for selected shift.');
      }

      // Settlement summary fields from GET /cash/shifts/{ss_id} response
      const openingFloat = parseFloat(cashShift.opening_float ?? 0);
      const totalCashSales = parseFloat(cashShift.total_cash_sales ?? 0);
      const manualPaidOut = parseFloat(cashShift.manual_paid_out_amount ?? 0);

      // Total Paid-Outs: manual_paid_out_amount on shift + sum of paid-outs list (GET /cash/paid-outs?ss_id=)
      let sumPaidOuts = 0;
      if (paidOutsResult.success && paidOutsResult.paidOuts) {
        sumPaidOuts = paidOutsResult.paidOuts.reduce((sum, po) => sum + parseFloat(po.amount ?? 0), 0);
      }
      const totalPaidOuts = manualPaidOut + sumPaidOuts;

      // Expected Cash: not from API — computed as Opening Float + Total Cash Sales − Total Paid-Outs
      const expectedCash = openingFloat + totalCashSales - totalPaidOuts;

      // Actual Cash (Counted): from GET /cash/drawers?ss_id= (drawer counts)
      let actualCash = 0;
      if (drawerCountsResult.success && drawerCountsResult.drawerCounts) {
        actualCash = drawerCountsResult.drawerCounts.reduce(
          (sum, drawer) => sum + parseFloat(drawer.total_amount ?? 0),
          0
        );
      }

      const variance = actualCash - expectedCash;

      // Audit trail: shift opened from cash shift, then drawer counts and paid-outs
      const auditTrail = [];
      if (cashShift.created_at) {
        auditTrail.push({
          action: 'Shift opened',
          timestamp: cashShift.created_at,
          user: cashShift.opened_by || 'System',
        });
      }

      if (drawerCountsResult.success && drawerCountsResult.drawerCounts.length > 0) {
        drawerCountsResult.drawerCounts.forEach((drawer, index) => {
          auditTrail.push({
            action: `Drawer count recorded: ${drawer.drawer_id || `Drawer ${index + 1}`}`,
            timestamp: drawer.created_at || new Date().toISOString(),
            user: drawer.recorded_by || 'Cashier',
          });
        });
      }

      if (paidOutsResult.success && paidOutsResult.paidOuts.length > 0) {
        paidOutsResult.paidOuts.forEach((paidOut) => {
          auditTrail.push({
            action: `Paid-out: ${paidOut.paid_out_reason || 'Paid-out'}`,
            timestamp: paidOut.created_at || new Date().toISOString(),
            user: paidOut.approved_by || 'Manager',
          });
        });
      }

      auditTrail.push({
        action: 'Settlement calculated',
        timestamp: new Date().toISOString(),
        user: 'System',
      });

      const settlement = {
        ss_id: selectedShiftId,
        opening_float: openingFloat,
        total_cash_sales: totalCashSales,
        expected_cash: expectedCash,
        counted_cash: actualCash,
        total_paid_outs: totalPaidOuts,
        discrepancy: variance,
        status: cashShift.status ?? 'open',
        audit_trail: auditTrail,
      };

      setSettlement(settlement);
    } catch (error) {
      console.error('Error calculating settlement:', error);
      setFinalizeFeedback({ 
        error: error.message || 'Failed to calculate settlement. Please try again.', 
        success: '' 
      });
      
      // Log API error
      loggingService.logError(
        'shift_reconciliation_calculate_settlement_error',
        `Failed to calculate settlement for shift ${selectedShiftId}: ${error.message || 'Unknown error'}`,
        error,
        {
          endpoint: '/cash/shifts',
          method: 'GET',
          shiftId: selectedShiftId,
        }
      ).catch(logError => {
        console.warn('[ShiftSettlementSummary] Failed to log error:', logError);
      });
      
      // Fallback to dummy data on error - COMMENTED OUT
      // const dummySettlement = DUMMY_SETTLEMENTS[selectedShiftId];
      // if (dummySettlement) {
      //   setSettlement(dummySettlement);
      // }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalize = async () => {
    if (!managerAcknowledged) {
      setFinalizeFeedback({ error: 'Please acknowledge the settlement by checking the box', success: '' });
      return;
    }

    if (!selectedShiftId) {
      setFinalizeFeedback({ error: 'Please select a shift', success: '' });
      return;
    }

    setIsFinalizing(true);
    setFinalizeFeedback({ success: '', error: '' });

    try {
      // if (USE_DUMMY_DATA) {
      //   // Simulate finalization with dummy data
      //   setTimeout(() => {
      //     setFinalizeFeedback({ success: 'Settlement finalized successfully!', error: '' });
      //     setManagerAcknowledged(false);
      //     // Update settlement status to finalized
      //     if (settlement) {
      //       setSettlement({
      //         ...settlement,
      //         status: 'finalized',
      //         audit_trail: [
      //           ...(settlement.audit_trail || []),
      //           {
      //             action: 'Settlement finalized',
      //             timestamp: new Date().toISOString(),
      //             user: 'Manager',
      //           },
      //         ],
      //       });
      //     }
      //     setIsFinalizing(false);
      //   }, 1000);
      //   return;
      // }

      // Update cash shift status to "closed"
      const updateResult = await cashSettlementApi.updateCashShift(selectedShiftId, {
        status: 'closed',
        manager_signature_digital: settlement?.manager_signature_digital || undefined,
      });

      if (updateResult.success) {
        setFinalizeFeedback({ success: 'Settlement finalized successfully!', error: '' });
        setManagerAcknowledged(false);
        
        // Log successful API call
        loggingService.logInfo(
          'shift_reconciliation_finalize',
          `Successfully finalized settlement for shift ${selectedShiftId}`,
          {
            endpoint: `/cash/shifts/${selectedShiftId}`,
            method: 'PUT',
            shiftId: selectedShiftId,
            discrepancy: settlement?.discrepancy || 0,
            hasSignature: !!settlement?.manager_signature_digital,
          }
        ).catch(logError => {
          console.warn('[ShiftSettlementSummary] Failed to log event:', logError);
        });
        
        // Refresh settlement data
        await calculateSettlement();
      } else {
        setFinalizeFeedback({ error: updateResult.error || 'Failed to finalize settlement', success: '' });
        
        // Log API error
        loggingService.logError(
          'shift_reconciliation_finalize_error',
          `Failed to finalize settlement for shift ${selectedShiftId}: ${updateResult.error || 'Unknown error'}`,
          null,
          {
            endpoint: `/cash/shifts/${selectedShiftId}`,
            method: 'PUT',
            shiftId: selectedShiftId,
          }
        ).catch(logError => {
          console.warn('[ShiftSettlementSummary] Failed to log error:', logError);
        });
      }
    } catch (error) {
      setFinalizeFeedback({ error: error.message || 'An error occurred', success: '' });
      
      // Log API error
      loggingService.logError(
        'shift_reconciliation_finalize_error',
        `Failed to finalize settlement for shift ${selectedShiftId}: ${error.message || 'Unknown error'}`,
        error,
        {
          endpoint: `/cash/shifts/${selectedShiftId}`,
          method: 'PUT',
          shiftId: selectedShiftId,
        }
      ).catch(logError => {
        console.warn('[ShiftSettlementSummary] Failed to log error:', logError);
      });
    } finally {
      setIsFinalizing(false);
    }
  };

  if (!settlement && !isLoading) {
    return (
      <div className="w-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Shift Reconciliation</h1>
          <p className="text-gray-600">View and finalize shift cash settlement</p>
        </div>

        <Card className="p-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Shift</label>
          {selectedShiftId && shifts.length > 0 ? (
            (() => {
              const shift = shifts.find((s) => s.ss_id === selectedShiftId) || shifts[0];
              const siteName =
                shift?.site_name ||
                sites.find((s) => (s.site_id || s.id) === (shift?.site_id || selectedSite))?.site_name ||
                sites.find((s) => (s.site_id || s.id) === (shift?.site_id || selectedSite))?.name ||
                'Unknown Site';
              const startTime = formatTimeFromTimestamp(shift?.shift_start_timestamp);
              const endTime = formatTimeFromTimestamp(shift?.shift_end_timestamp);
              return (
                <p className="text-gray-900 font-medium mb-6">
                  Shift at &quot;{siteName}&quot; from {startTime} to {endTime}
                </p>
              );
            })()
          ) : (
            <p className="text-gray-500 mb-6">No current shift. Please ensure you have an active shift from attendance.</p>
          )}

          <div className="text-center py-8 text-gray-500">
            {selectedShiftId ? 'Calculating settlement...' : 'No current shift to view settlement details.'}
          </div>
        </Card>
      </div>
    );
  }

  const discrepancy = settlement?.discrepancy || 0;
  const hasNegativeVariance = discrepancy < 0;

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Shift Reconciliation</h1>
        <p className="text-gray-600">View and finalize shift cash settlement</p>
      </div>

      {/* Current shift display (from API – read-only) */}
      <Card className="p-6 mb-6">
        {selectedShiftId && shifts.length > 0 ? (
          (() => {
            const shift = shifts.find((s) => s.ss_id === selectedShiftId) || shifts[0];
            const siteName =
              shift?.site_name ||
              sites.find((s) => (s.site_id || s.id) === (shift?.site_id || selectedSite))?.site_name ||
              sites.find((s) => (s.site_id || s.id) === (shift?.site_id || selectedSite))?.name ||
              'Unknown Site';
            const startTime = formatTimeFromTimestamp(shift?.shift_start_timestamp);
            const endTime = formatTimeFromTimestamp(shift?.shift_end_timestamp);
            return (
              <p className="text-gray-900 font-medium">
                Shift at &quot;{siteName}&quot; from {startTime} to {endTime}
              </p>
            );
          })()
        ) : (
          <p className="text-gray-500">No current shift. Please ensure you have an active shift from attendance.</p>
        )}
      </Card>

      {isLoading ? (
        <Card className="p-6">
          <div className="text-center py-8">Calculating settlement...</div>
        </Card>
      ) : settlement ? (
        <>
          {/* Settlement Summary */}
          <Card className="p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Settlement Summary</h2>

            <div className="space-y-4">
              {/* Expected Cash */}
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-lg font-medium text-gray-700">Opening Float</span>
                <span className="text-lg font-semibold text-gray-900">
                  ₹{parseFloat(settlement.opening_float || 0).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-lg font-medium text-gray-700">Total Cash Sales</span>
                <span className="text-lg font-semibold text-gray-900">
                  ₹{parseFloat(settlement.total_cash_sales || 0).toFixed(2)}
                </span>
              </div>

              {/* Total Paid-Outs (deducted from Opening + Sales to get Expected Cash) */}
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-lg font-medium text-gray-700">Total Paid-Outs</span>
                <span className="text-lg font-semibold text-red-700">
                  -₹{parseFloat(settlement.total_paid_outs || 0).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center py-3 border-b-2 border-gray-300">
                <span className="text-lg font-semibold text-gray-900">Expected Cash</span>
                <span className="text-xl font-bold text-blue-700">
                  ₹{parseFloat(settlement.expected_cash || 0).toFixed(2)}
                </span>
              </div>

              {/* Actual Cash (from drawer counts) */}
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-lg font-medium text-gray-700">Actual Cash (Counted)</span>
                <span className="text-lg font-semibold text-gray-900">
                  ₹{parseFloat(settlement.counted_cash || 0).toFixed(2)}
                </span>
              </div>

              {/* Discrepancy */}
              <div className={`flex justify-between items-center py-4 rounded-lg px-4 ${
                hasNegativeVariance ? 'bg-red-50 border-2 border-red-200' : 'bg-green-50 border-2 border-green-200'
              }`}>
                <div className="flex items-center gap-2">
                  {hasNegativeVariance ? (
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  ) : (
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  )}
                  <div className="flex flex-col">
                    <span className={`text-lg font-semibold ${hasNegativeVariance ? 'text-red-900' : 'text-green-900'}`}>
                      Discrepancy
                    </span>
                    <span className="text-xs text-gray-600 mt-1">
                      Actual Cash − Expected Cash
                    </span>
                  </div>
                </div>
                <span className={`text-2xl font-bold ${hasNegativeVariance ? 'text-red-700' : 'text-green-700'}`}>
                  {discrepancy >= 0 ? '+' : ''}₹{Math.abs(discrepancy).toFixed(2)}
                </span>
              </div>
            </div>
          </Card>

          {/* Audit Trail */}
          {settlement.audit_trail && settlement.audit_trail.length > 0 && (
            <Card className="p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Audit Trail</h2>
              <div className="space-y-2">
                {settlement.audit_trail.map((entry, index) => (
                  <div key={index} className="flex justify-between items-start py-2 border-b border-gray-100">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{entry.action}</p>
                      <p className="text-xs text-gray-500">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}</p>
                    </div>
                    {entry.user && (
                      <span className="text-xs text-gray-500">{entry.user}</span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Manager Acknowledgment */}
          <Card className="p-6 mb-6">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="manager-acknowledgment"
                checked={managerAcknowledged}
                onChange={(e) => setManagerAcknowledged(e.target.checked)}
                className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <label htmlFor="manager-acknowledgment" className="flex-1">
                <span className="text-sm font-semibold text-gray-900">
                  I acknowledge and approve this settlement
                </span>
                {hasNegativeVariance && (
                  <p className="text-xs text-red-600 mt-1">
                    Note: There is a negative variance. Please review before finalizing.
                  </p>
                )}
              </label>
            </div>
          </Card>

          {/* Feedback Messages */}
          {finalizeFeedback.error && (
            <div className="mb-6 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <XCircle className="w-5 h-5" />
              <span>{finalizeFeedback.error}</span>
            </div>
          )}

          {finalizeFeedback.success && (
            <div className="mb-6 flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
              <CheckCircle2 className="w-5 h-5" />
              <span>{finalizeFeedback.success}</span>
            </div>
          )}

          {/* Finalize Button */}
          <div className="flex justify-end">
            <PrimaryButton
              onClick={handleFinalize}
              disabled={isFinalizing || !managerAcknowledged || settlement.status === 'finalized'}
              className="w-full md:w-auto"
            >
              {isFinalizing ? 'Finalizing...' : settlement.status === 'finalized' ? 'Already Finalized' : 'Finalize Settlement'}
            </PrimaryButton>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ShiftSettlementSummary;
